import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TypingIndicatorProps {
    conversationId: string | null;
}

export const useTypingIndicator = ({ conversationId }: TypingIndicatorProps) => {
    const { user: currentUser } = useAuth();
    const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
    const subscriptionRef = useRef<any>(null);

    useEffect(() => {
        console.log('[useTypingIndicator] Mount for:', conversationId);
        if (!conversationId || !currentUser) return;

        const channel = supabase.channel(`typing:${conversationId}`);
        subscriptionRef.current = channel;

        channel
            .on('broadcast', { event: 'typing' }, ({ payload }) => {
                if (payload.userId !== currentUser.id && payload.isTyping) {
                    setTypingUsers((prev) => {
                        const newSet = new Set(prev);
                        newSet.add(payload.userId);
                        return newSet;
                    });

                    // Auto remove after 3 seconds if no more events
                    setTimeout(() => {
                        setTypingUsers((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(payload.userId);
                            return newSet;
                        });
                    }, 3000);
                }
            })
            .subscribe((status) => {
                console.log(`[TypingSubscription] Status for ${conversationId}:`, status);
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [conversationId, currentUser]);

    const sendTypingEvent = useCallback(() => {
        if (!conversationId || !currentUser || !subscriptionRef.current) return;

        const channel = subscriptionRef.current;
        if (channel) {
            console.log(`[TypingIndicator] Sending event to ${conversationId}`);
            channel.send({
                type: 'broadcast',
                event: 'typing',
                payload: { userId: currentUser.id, isTyping: true },
            }).catch((err: any) => console.error('[TypingIndicator] Send error:', err));
        }
    }, [conversationId, currentUser]);

    return {
        typingUsers: Array.from(typingUsers),
        sendTypingEvent,
        isTyping: typingUsers.size > 0
    };
};
